import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Property } from '@/types/property';
import { PropertyContract } from '@/hooks/useContracts';
import { Organization } from '@/hooks/useOrganizations';
import { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { filterActiveContracts } from '@/utils/contractUtils';
import PropertiesMultiSelect from './PropertiesMultiSelect';
import OrganizationsMultiSelect from './OrganizationsMultiSelect';

interface DashboardDesktopFiltersProps {
  monthOptions: { value: string; label: string }[];
  properties: Property[];
  organizations: Organization[];
  contracts: PropertyContract[];
  selectedMonth: string;
  selectedProperties: string[];
  selectedOrganizations: string[];
  selectedYear: string;
  setSelectedMonth: React.Dispatch<React.SetStateAction<string>>;
  setSelectedProperties: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedOrganizations: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedYear: React.Dispatch<React.SetStateAction<string>>;
  yearOptions: number[];
  isLoading?: boolean;
  goToPreviousMonth: () => void;
  goToNextMonth: () => void;
  canGoToPreviousMonth: boolean;
  canGoToNextMonth: boolean;
}

const DashboardDesktopFilters = ({
  monthOptions,
  properties,
  organizations,
  contracts,
  selectedMonth,
  selectedProperties,
  selectedOrganizations,
  selectedYear,
  setSelectedMonth,
  setSelectedProperties,
  setSelectedOrganizations,
  setSelectedYear,
  yearOptions,
  isLoading = false,
  goToPreviousMonth,
  goToNextMonth,
  canGoToPreviousMonth,
  canGoToNextMonth,
}: DashboardDesktopFiltersProps) => {

  // Filtrar propiedades basadas en organizaciones seleccionadas (cascada)
  const filteredProperties = useMemo(() => {
    console.log('=== Dashboard Filter Debug ===');
    console.log('selectedOrganizations:', selectedOrganizations);
    console.log('contracts count:', contracts?.length);
    console.log('properties count:', properties?.length);
    
    if (selectedOrganizations.length === 0) {
      const activePropertyIds = new Set(
        filterActiveContracts(contracts || []).map(c => c.property_id)
      );
      return properties.filter(p => activePropertyIds.has(p.id));
    }
    
    const activeContracts = filterActiveContracts(contracts || []);
    console.log('activeContracts count:', activeContracts.length);
    
    // Debug: mostrar todos los organization_id de los contratos activos
    activeContracts.forEach(c => {
      console.log(`Contract: property=${c.property_id}, org_id=${c.organization_id}, type=${typeof c.organization_id}`);
    });
    
    const propertyIdsInOrgs = activeContracts
      .filter(c => {
        const orgId = c.organization_id || '';
        const match = selectedOrganizations.includes(orgId);
        console.log(`Checking contract org=${orgId} against selected=${selectedOrganizations.join(',')}: match=${match}`);
        return match;
      })
      .map(c => c.property_id);
    
    console.log('propertyIdsInOrgs:', propertyIdsInOrgs);
    
    const result = properties.filter(p => propertyIdsInOrgs.includes(p.id));
    console.log('filtered properties count:', result.length);
    
    return result;
  }, [properties, contracts, selectedOrganizations]);

  return (
    <>
      {/* Left group: Organizations and Properties */}
      <div className="flex items-center gap-2">
        {isLoading ? (
          <Skeleton className="h-10 w-[200px]" />
        ) : (
          <OrganizationsMultiSelect
            organizations={organizations}
            selectedOrganizations={selectedOrganizations}
            onSelectionChange={setSelectedOrganizations}
          />
        )}
        {isLoading ? (
          <Skeleton className="h-10 w-[200px]" />
        ) : (
          <PropertiesMultiSelect
            properties={filteredProperties}
            selectedProperties={selectedProperties}
            onSelectionChange={setSelectedProperties}
          />
        )}
      </div>

      {/* Right group: Year and Month with navigation */}
      <div className="flex items-center gap-2">
        <Select value={selectedYear} onValueChange={setSelectedYear}>
          <SelectTrigger className="w-24 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0 text-foreground">
            <SelectValue />
          </SelectTrigger>

          <SelectContent>
            {yearOptions.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="ghost"
          size="icon"
          onClick={goToPreviousMonth}
          disabled={!canGoToPreviousMonth}
          className="h-8 w-8 rounded-full"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-32 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0 text-foreground">
            <SelectValue />
          </SelectTrigger>

          <SelectContent>
            {monthOptions.map((month) => (
              <SelectItem key={month.value} value={month.value}>
                {month.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="ghost"
          size="icon"
          onClick={goToNextMonth}
          disabled={!canGoToNextMonth}
          className="h-8 w-8 rounded-full"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </>
  );
};

export default DashboardDesktopFilters;
